import type { TFunction } from 'i18next'
import type {
  FieldErrors,
  FieldValues,
  Resolver as HookFormResolver,
} from 'react-hook-form'
import type { z } from 'zod'
import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers'

import { merge } from '@mntn-dev/utilities'
import type { ZodInfer } from '@mntn-dev/utility-types'

import { isZodError } from '../utils/is-zod-error.ts'
import { parseErrorSchema } from '../utils/parse-error-schema.ts'
import {
  defaultRuleMapFactory,
  type ErrorMapWithContext,
  type FormFieldData,
} from '../validation/index.ts'
import type { ZodParseOptions, ZodResolverOptions } from './types.ts'

export function zodInternalResolver<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
>(
  schema: TSchema,
  tFunction: TFunction<'validation'>,
  schemaOptions?: ZodParseOptions,
  resolverOptions?: ZodResolverOptions<TResolvedValues>
) {
  return (async (values, context, options) => {
    const baseErrorMap: ErrorMapWithContext =
      (schemaOptions?.error as ErrorMapWithContext | undefined) ??
      (schemaOptions?.errorMap as ErrorMapWithContext | undefined) ??
      defaultRuleMapFactory(tFunction)
    const {
      error: _ignoredError,
      errorMap: _ignoredErrorMap,
      ...parseContext
    } = schemaOptions ?? {}
    const input = merge(values, { context })
    const fields = options.names?.reduce<
      Record<keyof TFieldValues, FormFieldData>
    >(
      (fieldMap, fieldName) => {
        const formData = options.fields[fieldName]
        fieldMap[fieldName] = {
          ...formData,
          label:
            resolverOptions?.fieldLabelConfig?.getFieldLabel(fieldName) ??
            'Field',
        }

        return fieldMap
      },
      {} as Record<keyof TFieldValues, FormFieldData>
    )

    try {
      const data = await schema[
        resolverOptions?.mode === 'async' ? 'parseAsync' : 'parse'
      ](input, {
        ...parseContext,
        error: (issue) => {
          const result = baseErrorMap(issue as z.ZodIssue, {
            ...(context as Record<string, unknown>),
            ...(fields ? { fields } : {}),
          })

          if (typeof result === 'string') {
            return { message: result }
          }

          if (result?.message) {
            return result
          }

          return { message: issue.message ?? 'Invalid value' }
        },
      })

      options.shouldUseNativeValidation && validateFieldsNatively({}, options)

      return {
        errors: {} as FieldErrors<TFieldValues>,
        values: data as TResolvedValues,
      }
    } catch (error) {
      if (isZodError(error)) {
        const parsedErrors = parseErrorSchema(
          error.issues,
          !options.shouldUseNativeValidation && options.criteriaMode === 'all'
        )

        const errors = toNestErrors(parsedErrors, options)

        return {
          values: {},
          errors,
        }
      }

      throw error
    }
  }) as HookFormResolver<TFieldValues, TContext, TResolvedValues>
}

export function zodResolver<
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
>(
  schema: TSchema,
  tFunction: TFunction<'validation'>,
  schemaOptions?: ZodParseOptions,
  resolverOptions?: ZodResolverOptions<TResolvedValues>
) {
  return { schema, tFunction, schemaOptions, resolverOptions }
}
